import { useCallback, useMemo } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import _, { get } from "lodash"

import { convertMapViewObjectToPath } from "utils/map"
import { useVenue } from "providers/venue"
import { useGeoLocation } from "providers/geolocation"

import { useFeatureQuickSearch } from "hooks/feature/useFeatureQuickSearch"

import { DEFAULT_VENUE_FILTER, Q_MAP_VIEW, Q_VENUE } from "constant"

import { useVenueController } from "hooks/venue/useVenueController"
import { useGetVenueView } from "./useGetVenueView"

export const useMap = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const venueViews = useGetVenueView()
  const { isMultiVenueProject, levels, findFeatureById } = useVenue()
  const { locationVenue, locationOrdinal } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: viewingVenueId, changeViewingVenue } =
    useVenueController(locationVenueId)
  const navigate = useNavigate()
  const { category } = useParams()
  const viewingOrdinal = useMemo(
    () => searchParams.get("ordinal"),
    [searchParams]
  )

  const comingFromOrdinal = useMemo(
    () => searchParams.get("from-ordinal"),
    [searchParams]
  )

  const { categories: quickSearchCategories, features: quickSearchFeatures } =
    useFeatureQuickSearch(category, viewingVenueId)

  const sortedLevels = useMemo(
    () => _.orderBy(levels, "properties.ordinal", "desc"),
    [levels]
  )

  const viewingVenueLevels = useMemo(
    () =>
      // Temporary condition should later be changed to properties.venue.id when prepareFeature has an ability to bindVenue
      isMultiVenueProject
        ? _(sortedLevels)
            .filter(
              //! Temporary solution to display the ordinal selector when viewingVenueId is set to "all".
              (level) =>
                viewingVenueId === DEFAULT_VENUE_FILTER ||
                get(level, "properties.venue_id") === viewingVenueId
            )
            .uniqBy("properties.name.en")
            .value()
        : sortedLevels,
    [isMultiVenueProject, sortedLevels, viewingVenueId]
  )

  const viewingVenueView = useMemo(
    () => get(venueViews, viewingVenueId, venueViews[DEFAULT_VENUE_FILTER]),
    [venueViews, viewingVenueId]
  )

  const findNearestOrdinal = useCallback(
    (viewingOrdinal) => {
      const parsedOrdinal = !_.isNil(viewingOrdinal)
        ? Number(viewingOrdinal)
        : 0
      let nearestOrdinal = parsedOrdinal
      const nearestFeature = _.minBy(quickSearchFeatures, (amenity) =>
        Math.abs(amenity.properties.ordinal - parsedOrdinal)
      )
      if (nearestFeature) {
        nearestOrdinal = nearestFeature.properties.ordinal
      }

      return nearestOrdinal
    },
    [quickSearchFeatures]
  )

  const onChangeVenue = useCallback(
    (venueId) => {
      searchParams.set(Q_VENUE, venueId)

      const mapViewToUpdate = get(
        venueViews,
        venueId,
        venueViews[DEFAULT_VENUE_FILTER]
      )
      const mapViewPath = convertMapViewObjectToPath(mapViewToUpdate)
      if (mapViewPath) searchParams.set(Q_MAP_VIEW, mapViewPath)

      navigate(
        `/maps/ordinal/0${
          venueId !== null ? `?${searchParams.toString()}` : ""
        }`,
        {
          replace: true,
        }
      )
    },
    [navigate, venueViews, searchParams]
  )
  const toggleQuickSearch = useCallback(
    (type, featureCategory) => {
      //This condition prevent navigation when viewingOrdinal or viewingVenueId is undefined
      if (_.isNil(viewingVenueId) || _.isNil(viewingOrdinal)) return
      if (featureCategory === category) {
        return navigate(
          `/maps/ordinal/${viewingOrdinal}?venue=${viewingVenueId}`
        )
      }
      navigate(
        `/maps/${type}/${featureCategory}?venue=${viewingVenueId}&from-ordinal=${viewingOrdinal}`
      )
    },
    [navigate, viewingOrdinal, category, viewingVenueId]
  )

  const viewingVenue = useMemo(
    () => findFeatureById(viewingVenueId),
    [viewingVenueId, findFeatureById]
  )

  return {
    locationVenueId,
    locationOrdinal,
    viewingVenue,
    viewingVenueId,
    sortedLevels,
    viewingVenueLevels,
    quickSearchFeatures,
    quickSearchCategories,
    viewingOrdinal,
    searchParams,
    category,
    comingFromOrdinal,
    viewingVenueView,
    venueViews,
    // Function
    findNearestOrdinal,
    onChangeVenue,
    setSearchParams,
    toggleQuickSearch,
    changeViewingVenue,
  }
}
